import { getData as getRequirements } from "../models/RequirementsModel";
import { getData as getCandidates } from "../models/CandidatesModel";
import { getData as getInterviews } from "../models/InterviewsModel";
import { getData as getOnboards } from "../models/OnboardsModel";
import dateFormat from "dateformat";

export function buildSelectOption(value, label) {
  if (value == null || value === "") return "";
  return {
    value: Number(value) || value,
    label: label || `#${value}`,
  };
}

export function getPipelineUrlParams(searchParams) {
  return {
    clientId: searchParams.get("_client_id"),
    requirementId: searchParams.get("_requirement_id"),
    candidateId: searchParams.get("_candidate_id"),
    interviewId: searchParams.get("_interview_id"),
  };
}

export function hasPipelineUrlContext(searchParams) {
  const { clientId, requirementId, candidateId, interviewId } =
    getPipelineUrlParams(searchParams);
  return Boolean(clientId || requirementId || candidateId || interviewId);
}

export function buildPipelinePrefillFromRequirement(row) {
  if (!row?._requirement_id) return null;
  return {
    clientOption: buildSelectOption(row.requirement_client_id, row.client_name),
    requirementOption: buildSelectOption(row._requirement_id, row.requirement_name),
    chips: [
      {
        key: "requirement",
        label: `Requirement: ${row.requirement_name}`,
      },
    ],
  };
}

export function buildPipelineSearchParams(row) {
  const params = new URLSearchParams();
  if (row._requirement_id) {
    params.set("_requirement_id", row._requirement_id);
  }
  if (row.requirement_client_id) {
    params.set("_client_id", row.requirement_client_id);
  }
  return params.toString();
}

export async function findRequirementInClient(user_token, clientId, requirementId) {
  if (!user_token || !clientId || !requirementId) return null;

  const response = await getRequirements({
    user_token,
    requirement_client_id: clientId,
    page: 1,
    limit: -1,
  });

  return (
    (response?.requirements || []).find(
      (row) => String(row._requirement_id) === String(requirementId)
    ) || null
  );
}

/** Fallback for legacy links that only pass _requirement_id */
export async function findRequirementById(user_token, requirementId) {
  if (!user_token || !requirementId) return null;
  const response = await getRequirements({ user_token, page: 1, limit: -1 });
  return (
    (response?.requirements || []).find(
      (row) => String(row._requirement_id) === String(requirementId)
    ) || null
  );
}

export async function findCandidateById(user_token, candidateId) {
  if (!user_token || !candidateId) return null;
  const response = await getCandidates({
    user_token,
    page: 1,
    limit: -1,
    include_inactive_mappings: 1,
  });
  return (
    (response?.candidates || []).find(
      (row) => String(row._candidate_id) === String(candidateId)
    ) || null
  );
}

export async function resolvePipelineUrlContext(user_token, searchParams, clients = []) {
  const { clientId, requirementId, candidateId, interviewId } =
    getPipelineUrlParams(searchParams);
  const chips = [];
  let clientOption = "";
  let requirementOption = "";

  if (requirementId) {
    let req = null;

    if (clientId) {
      req = await findRequirementInClient(user_token, clientId, requirementId);
    } else {
      req = await findRequirementById(user_token, requirementId);
    }

    if (req) {
      const existingClient = clients.find(
        (c) => String(c.value) === String(req.requirement_client_id)
      );
      clientOption =
        existingClient ||
        buildSelectOption(req.requirement_client_id, req.client_name);
      requirementOption = buildSelectOption(req._requirement_id, req.requirement_name);
      chips.push({
        key: "requirement",
        label: `Requirement: ${req.requirement_name}`,
      });
    } else if (clientId) {
      const existingClient = clients.find(
        (c) => String(c.value) === String(clientId)
      );
      clientOption =
        existingClient || buildSelectOption(clientId, `Client #${clientId}`);
      requirementOption = buildSelectOption(requirementId, `Requirement #${requirementId}`);
      chips.push({
        key: "requirement",
        label: `Requirement #${requirementId}`,
      });
    }
  } else if (clientId) {
    const existingClient = clients.find((c) => String(c.value) === String(clientId));
    if (existingClient) {
      clientOption = existingClient;
      chips.push({ key: "client", label: `Client: ${existingClient.label}` });
    }
  }

  if (candidateId) {
    const candidate = await findCandidateById(user_token, candidateId);
    if (candidate) {
      chips.push({
        key: "candidate",
        label: `Candidate: ${candidate.candidate_firstname} ${candidate.candidate_lastname}`.trim(),
      });
    } else {
      chips.push({ key: "candidate", label: `Candidate #${candidateId}` });
    }
  }

  if (interviewId) {
    chips.push({ key: "interview", label: `Interview #${interviewId}` });
  }

  return {
    hasContext: chips.length > 0,
    clientOption,
    requirementOption,
    chips,
  };
}

/** Clamp experience for onboard/candidate float inputs (allows one decimal). */
export function normalizeExperienceYearsForForm(value) {
  if (value == null || value === "") return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return trimmed;
  const capped = Math.min(99.9, Math.max(0, n));
  const oneDecimal = Math.round(capped * 10) / 10;
  return String(oneDecimal);
}

export function normalizeOnboardExperienceFields(fields) {
  if (!fields || typeof fields !== "object") return fields;
  return {
    ...fields,
    onboard_total_exp: normalizeExperienceYearsForForm(fields.onboard_total_exp),
    onboard_relevant_exp: normalizeExperienceYearsForForm(
      fields.onboard_relevant_exp
    ),
  };
}

export function mapCandidateToOnboardPrefill(candidate) {
  if (!candidate) return {};
  const name = `${candidate.candidate_firstname || ""} ${candidate.candidate_lastname || ""}`.trim();
  return {
    onboard_candidate_id: buildSelectOption(candidate._candidate_id, name),
    onboard_client_id: buildSelectOption(
      candidate.candidate_client_id,
      candidate.client_name
    ),
    onboard_requirement_id: buildSelectOption(
      candidate.candidate_requirement_id,
      candidate.requirement_name
    ),
    onboard_total_exp: normalizeExperienceYearsForForm(
      candidate.candidate_experience_total
    ),
    onboard_relevant_exp: normalizeExperienceYearsForForm(
      candidate.candidate_experience_relevant
    ),
    onboard_previous_employer: candidate.candidate_current_company || "",
    onboard_notice_period: candidate.candidate_notice_period || "",
    onboard_previous_ctc: candidate.candidate_current_ctc || "",
    onboard_expected_ctc: candidate.candidate_offered_ctc || "",
    onboard_offered_ctc: candidate.candidate_offered_ctc || "",
    onboard_present_location: candidate.candidate_present_location || "",
  };
}

/** Identity + mapping only — does not overwrite compensation fields on candidate change */
export function mapCandidateToOnboardIdentityPrefill(candidate) {
  const { onboard_offered_ctc, onboard_expected_ctc, ...identity } =
    mapCandidateToOnboardPrefill(candidate);
  return identity;
}

/** Candidate list params for onboard — all requirement types (C2H + FTE), optionally scoped */
export function buildOnboardCandidateListParams(userToken, { clientId, requirementId } = {}) {
  const params = {
    user_token: userToken,
    page: 1,
    limit: -1,
    include_inactive_mappings: 1,
  };
  if (requirementId) {
    params.candidate_requirement_id = requirementId;
  } else if (clientId) {
    params.candidate_client_id = clientId;
  }
  return params;
}

export async function findInterviewById(userToken, interviewId) {
  if (!userToken || !interviewId) return null;
  const response = await getInterviews({
    user_token: userToken,
    page: 1,
    limit: 1,
    _interview_id: interviewId,
  });
  return response?.interviews?.[0] || null;
}

export async function findOnboardByInterviewId(userToken, interviewId) {
  if (!userToken || !interviewId) return null;
  const response = await getOnboards({
    user_token: userToken,
    page: 1,
    limit: 1,
    onboard_interview_id: interviewId,
  });
  return response?.onboards?.[0] || null;
}

export function buildInterviewLinkLabel(interview) {
  if (!interview) return "Interview";
  const parts = [
    interview.interview_round_name,
    interview.status_name,
    interview.interview_date
      ? dateFormat(interview.interview_date, "dd mmm yyyy")
      : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : `Interview #${interview._interview_id}`;
}

export function buildOnboardPrefillFromInterview(interview) {
  if (!interview?._interview_id) return {};
  return {
    interviewId: interview._interview_id,
    candidateId: interview.interview_candidate_id,
    clientId: interview.interview_client_id,
    clientName: interview.client_name,
    requirementId: interview.interview_requirement_id,
    requirementName: interview.requirement_name,
  };
}
